import React, { useState, useCallback, useEffect } from 'react';
import DOMPurify from 'dompurify';
import toast from 'react-hot-toast';
import type { AxiosProgressEvent } from 'axios';
import StepCard from '../../../courses/wizard/shared/StepCard';
import DropZone from '../../../courses/wizard/shared/DropZone';
import { useCreateProgram, useUpdateProgram } from '../../../../services/programs';
import { uploadFile, getSignedUrl } from '../../../../services/uploads';
import { palette } from '@/styles/figma-tokens';

// ─── Constants ────────────────────────────────────────────────────────────────

const MAX_IMAGE_SIZE_MB = 5;
const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

// ─── Inline field styles (matching course wizard Figma spec) ──────────────────

const inputStyle: React.CSSProperties = {
  width: '100%',
  border: '1px solid rgb(var(--color-border-primary) / 0.45)',
  borderRadius: '8px',
  padding: '14px 16px',
  fontFamily: 'Poppins, sans-serif',
  fontSize: '15px',
  lineHeight: '24px',
  color: 'rgb(var(--color-text-primary))',
  background: 'rgb(var(--color-surface-primary))',
  outline: 'none',
  boxSizing: 'border-box',
  transition: 'border-color 0.15s',
};

const inputErrorStyle: React.CSSProperties = {
  ...inputStyle,
  borderColor: palette.error['500'],
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontFamily: 'Poppins, sans-serif',
  fontSize: '12px',
  fontWeight: 600,
  lineHeight: '18px',
  color: 'rgb(var(--color-text-secondary))',
  marginBottom: '6px',
};

const errorTextStyle: React.CSSProperties = {
  fontFamily: 'Poppins, sans-serif',
  fontSize: '12px',
  lineHeight: '18px',
  color: palette.error['500'],
  marginTop: '4px',
};

// ─── Types ────────────────────────────────────────────────────────────────────

export interface ProgramInitialData {
  name: string;
  description: string | null;
  header_image: string | null;
}

interface ProgramDetailsStepProps {
  /** null = create mode, non-null = edit mode */
  programId: string | null;
  /** Pre-populated data for edit mode */
  initialData?: ProgramInitialData;
  disabled?: boolean;
  onSaved: (programId: string) => void;
  onCancel?: () => void;
  /** Stagger delay forwarded to StepCard (seconds). */
  entranceDelay?: number;
}

interface FormErrors {
  name?: string;
}

// ─── Image upload state ───────────────────────────────────────────────────────

interface FileUploadState {
  /** The S3 key stored in form state (sent to the API) */
  key: string | null;
  /** A local blob URL or signed S3 URL used only for preview rendering */
  previewUrl: string | null;
  fileName: string | null;
  fileSize: number | null;
  uploading: boolean;
  uploadProgress: number;
  uploadError: string | null;
}

function emptyFileState(): FileUploadState {
  return {
    key: null,
    previewUrl: null,
    fileName: null,
    fileSize: null,
    uploading: false,
    uploadProgress: 0,
    uploadError: null,
  };
}

// ─── Image preview component ──────────────────────────────────────────────────

function ImagePreview({
  state,
  onRemove,
}: {
  state: FileUploadState;
  onRemove: () => void;
}) {
  return (
    <div
      style={{
        borderRadius: '16px',
        border: '1px solid rgb(var(--color-border-primary) / 0.25)',
        overflow: 'hidden',
        background: 'rgb(var(--color-surface-primary))',
      }}
    >
      <div
        style={{
          width: '100%',
          maxHeight: '200px',
          overflow: 'hidden',
          background: 'rgb(var(--color-surface-elevated))',
        }}
      >
        <img
          src={state.previewUrl!}
          alt="Program header preview"
          style={{ width: '100%', height: '200px', objectFit: 'cover', display: 'block' }}
        />
      </div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '12px 16px',
          gap: '12px',
        }}
      >
        <div style={{ minWidth: 0 }}>
          <p
            style={{
              fontFamily: 'Poppins, sans-serif',
              fontSize: '13px',
              fontWeight: 600,
              color: 'rgb(var(--color-text-primary))',
              margin: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {state.fileName}
          </p>
          {state.fileSize !== null && (
            <p
              style={{
                fontFamily: 'Poppins, sans-serif',
                fontSize: '12px',
                color: 'rgb(var(--color-text-secondary))',
                margin: '2px 0 0',
              }}
            >
              {(state.fileSize / 1024 / 1024).toFixed(2)} MB
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={onRemove}
          disabled={state.uploading}
          className="active:scale-[0.97] transition-[opacity,transform] duration-150"
          style={{
            flexShrink: 0,
            background: 'none',
            border: 'none',
            padding: '4px 8px',
            fontFamily: 'Poppins, sans-serif',
            fontSize: '13px',
            fontWeight: 600,
            color: palette.error['500'],
            cursor: state.uploading ? 'not-allowed' : 'pointer',
            opacity: state.uploading ? 0.5 : 1,
          }}
        >
          Remove
        </button>
      </div>
      {state.uploading && (
        <div style={{ padding: '0 16px 12px' }}>
          <div
            style={{
              height: '4px',
              background: 'rgb(var(--color-primary) / 0.15)',
              borderRadius: '2px',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                height: '100%',
                width: `${state.uploadProgress}%`,
                background: 'rgb(var(--color-primary))',
                borderRadius: '2px',
                transition: 'width 0.2s',
              }}
            />
          </div>
          <p
            style={{
              fontFamily: 'Poppins, sans-serif',
              fontSize: '12px',
              color: 'rgb(var(--color-text-secondary))',
              margin: '4px 0 0',
            }}
          >
            Uploading... {state.uploadProgress}%
          </p>
        </div>
      )}
    </div>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * ProgramDetailsStep — Step 1 of the program creation/edit wizard ("Basics").
 * Owns name, description, and header image. Duration (and the day slots it
 * controls) moved to Step 2 ("Days & Courses"); certification is Step 3;
 * audiences moved to /programs/:id/configure.
 */
export default function ProgramDetailsStep({
  programId,
  initialData,
  disabled = false,
  onSaved,
  onCancel,
  entranceDelay,
}: ProgramDetailsStepProps) {
  // ── Form fields ──────────────────────────────────────────────────────────
  const [name, setName] = useState(initialData?.name ?? '');
  const [description, setDescription] = useState(initialData?.description ?? '');
  const [errors, setErrors] = useState<FormErrors>({});

  // ── Header image state ────────────────────────────────────────────────────
  const [headerImage, setHeaderImage] = useState<FileUploadState>({
    key: initialData?.header_image ?? null,
    previewUrl: null,
    fileName: initialData?.header_image ? 'Program header' : null,
    fileSize: null,
    uploading: false,
    uploadProgress: 0,
    uploadError: null,
  });

  // ── Save state ───────────────────────────────────────────────────────────
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const createProgram = useCreateProgram();
  const updateProgram = useUpdateProgram();

  // ── Edit mode: resolve existing image key to a signed URL for preview ────
  useEffect(() => {
    const imageKey = initialData?.header_image;
    if (!imageKey) return;
    let cancelled = false;
    getSignedUrl({ key: imageKey })
      .then((result) => {
        if (!cancelled) setHeaderImage((prev) => ({ ...prev, previewUrl: result.url }));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [initialData?.header_image]);

  // ── Validation ───────────────────────────────────────────────────────────
  const validate = (): FormErrors => {
    const errs: FormErrors = {};
    const trimmedName = name.trim();
    if (!trimmedName) errs.name = 'Name is required';
    else if (trimmedName.length < 3) errs.name = 'Name must be at least 3 characters';
    else if (trimmedName.length > 200) errs.name = 'Name must not exceed 200 characters';

    return errs;
  };

  // ── Header image upload ──────────────────────────────────────────────────
  const handleHeaderImageSelected = useCallback(
    async (files: File[]) => {
      const file = files[0];
      if (!file) return;
      if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
        setHeaderImage((prev) => ({
          ...prev,
          uploadError: 'Only JPG, PNG, and WebP images are allowed',
        }));
        return;
      }
      const localPreview = URL.createObjectURL(file);
      setHeaderImage({
        key: null,
        previewUrl: localPreview,
        fileName: file.name,
        fileSize: file.size,
        uploading: true,
        uploadProgress: 0,
        uploadError: null,
      });
      try {
        const resourceId = programId ?? crypto.randomUUID();
        const result = await uploadFile(
          { file, resource_type: 'program_header', resource_id: resourceId },
          {
            onUploadProgress: (progressEvent: AxiosProgressEvent) => {
              const pct = Math.round(
                (progressEvent.loaded * 100) / (progressEvent.total || 100)
              );
              setHeaderImage((prev) => ({ ...prev, uploadProgress: pct }));
            },
          }
        );
        setHeaderImage((prev) => ({
          ...prev,
          key: result.key,
          uploading: false,
          uploadProgress: 100,
        }));
      } catch (err: unknown) {
        URL.revokeObjectURL(localPreview);
        const message =
          (err as { response?: { data?: { message?: string } } })?.response?.data
            ?.message ?? 'Upload failed. Please try again.';
        setHeaderImage({ ...emptyFileState(), uploadError: message });
      }
    },
    [programId]
  );

  const handleRemoveHeaderImage = () => {
    if (headerImage.previewUrl) URL.revokeObjectURL(headerImage.previewUrl);
    setHeaderImage(emptyFileState());
  };

  // ── Save handler ─────────────────────────────────────────────────────────
  const handleSave = async () => {
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;

    const sanitize = (value: string) =>
      DOMPurify.sanitize(value.trim(), { ALLOWED_TAGS: [], ALLOWED_ATTR: [] });

    const basePayload = {
      name: sanitize(name),
      description: sanitize(description) || null,
      header_image: headerImage.key ?? null,
    };

    setSaving(true);
    try {
      if (programId) {
        // Duration lives in Step 2 now — never overwrite it from Basics on edit.
        await updateProgram.mutateAsync({ programId, data: basePayload });
        setSaved(true);
        onSaved(programId);
      } else {
        // New programs start as a single day; the real duration (and the day
        // slots it controls) is set in Step 2 ("Days & Courses").
        const program = await createProgram.mutateAsync({ ...basePayload, duration_days: 1 });
        setSaved(true);
        onSaved(program.id);
      }
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        'Failed to save program. Please try again.';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const trimmedName = name.trim();
  const summary = saved && trimmedName ? trimmedName : undefined;

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <StepCard
      stepNumber={1}
      title="Basics"
      disabled={disabled}
      onSave={handleSave}
      onCancel={onCancel}
      saving={saving}
      saved={saved}
      summary={summary}
      entranceDelay={entranceDelay}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
        {/* Row 1: Program name */}
        <div>
          <label htmlFor="program-name" style={labelStyle}>
            Add Program Name
          </label>
          <input
            id="program-name"
            type="text"
            placeholder="Add Program Name"
            value={name}
            maxLength={200}
            onChange={(e) => {
              setName(e.target.value);
              if (errors.name) setErrors((prev) => ({ ...prev, name: undefined }));
            }}
            style={errors.name ? inputErrorStyle : inputStyle}
            onFocus={(e) => {
              (e.currentTarget as HTMLInputElement).style.borderColor = errors.name
                ? palette.error['500']
                : 'rgb(var(--color-primary))';
            }}
            onBlur={(e) => {
              (e.currentTarget as HTMLInputElement).style.borderColor = errors.name
                ? palette.error['500']
                : 'rgb(var(--color-border-primary) / 0.45)';
            }}
          />
          {errors.name && (
            <p role="alert" style={errorTextStyle}>
              {errors.name}
            </p>
          )}
        </div>

        {/* Row 2: Description */}
        <div>
          <label htmlFor="program-description" style={labelStyle}>
            Program Description
          </label>
          <textarea
            id="program-description"
            placeholder="Describe what learners will achieve..."
            value={description}
            rows={4}
            onChange={(e) => setDescription(e.target.value)}
            style={{
              ...inputStyle,
              resize: 'vertical',
              minHeight: '96px',
            }}
            onFocus={(e) => {
              (e.currentTarget as HTMLTextAreaElement).style.borderColor = 'rgb(var(--color-primary))';
            }}
            onBlur={(e) => {
              (e.currentTarget as HTMLTextAreaElement).style.borderColor = 'rgb(var(--color-border-primary) / 0.45)';
            }}
          />
        </div>

        {/* Row 3: Header image */}
        <div>
          {headerImage.previewUrl ? (
            <ImagePreview state={headerImage} onRemove={handleRemoveHeaderImage} />
          ) : (
            <DropZone
              onFilesSelected={handleHeaderImageSelected}
              accept="image/jpeg,image/png,image/webp"
              maxSizeMB={MAX_IMAGE_SIZE_MB}
              label="Drop or select cover image"
            />
          )}
          {headerImage.uploadError && (
            <p role="alert" style={{ ...errorTextStyle, marginTop: '8px' }}>
              {headerImage.uploadError}
            </p>
          )}
        </div>
      </div>
    </StepCard>
  );
}
